import { useEffect, useState } from 'react';

function AccountAccessPanel({ mode, onBack }) {
  const [stage, setStage] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const isRegistration = mode === 'register';
  const isRecovery = mode === 'forgot';
  const purpose = isRegistration ? 'activate' : 'reset';
  const isLegacyLink = !isRegistration && !isRecovery;

  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('maccez_account_code_pending') || 'null');
    const resume = draft?.mode === mode && draft.expires > Date.now();
    setStage(resume ? 'code' : 'email');
    setEmail(resume ? draft.email : '');
    setCode('');
    setMessage('');
  }, [mode]);

  async function request(endpoint, payload) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'No se pudo completar la solicitud');
    return data;
  }

  async function sendCode() {
    setLoading(true);
    setMessage('');
    try {
      const data = await request(
        isRegistration ? '/api/auth/register' : '/api/auth/forgot-password',
        { email: email.trim().toLowerCase() }
      );
      localStorage.setItem('maccez_account_code_pending', JSON.stringify({
        mode, email: email.trim().toLowerCase(), expires: Date.now() + 600000
      }));
      setStage('code');
      setCode('');
      setMessage(data.message || 'Revisa tu correo e ingresa el código');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (loading || isLegacyLink) return;
    if (stage === 'email') return sendCode();
    setLoading(true);
    setMessage('');
    try {
      if (stage === 'code') {
        await request('/api/auth/verify-code', {
          email: email.trim().toLowerCase(), code, purpose
        });
        setStage('details');
        return;
      }
      if (password !== confirmation) {
        setMessage('Las contraseñas no coinciden');
        return;
      }
      await request(
        isRegistration ? '/api/auth/activate' : '/api/auth/reset-password',
        {
          email: email.trim().toLowerCase(), code,
          password, confirm_password: confirmation,
          ...(isRegistration ? {
            name: name.trim(), phone: phone.trim(), address: address.trim()
          } : {})
        }
      );
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('maccez_account_code_pending');
      window.location.replace(isRegistration
        ? '/?cuenta=activada' : '/?cuenta=recuperada');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  const input = {
    boxSizing: 'border-box', display: 'block', width: '100%',
    marginTop: 6, padding: '10px 12px', borderRadius: 10,
    border: '1px solid #64748b', background: '#0f172a',
    color: '#fff', fontSize: 16
  };
  const label = { display: 'block', marginBottom: 12, color: '#e2e8f0',
    fontSize: 13 };
  const field = (title, value, setter, options = {}) => (
    <label style={label}>
      {title}
      <input value={value} onChange={(event) => setter(event.target.value)}
        required style={input} {...options} />
    </label>
  );

  return (
    <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
      <h3 style={{ color: '#fff', margin: '0 0 8px', fontSize: 22 }}>
        {isRegistration ? 'Crear cuenta' : 'Recuperar contraseña'}
      </h3>
      {isLegacyLink ? (
        <p style={{ color: '#cbd5e1', fontSize: 14 }}>
          Este enlace ya no se usa. Abre la aplicación y solicita un código nuevo
          desde Crear cuenta u Olvidé contraseña.
        </p>
      ) : (
        <>
          <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.4,
            margin: '0 0 15px' }}>
            {stage === 'email'
              ? 'Ingresa el correo habilitado. Recibirás un código para continuar aquí.'
              : stage === 'code'
                ? `Escribe el código de seis dígitos enviado a ${email}. Vence en 10 minutos.`
                : isRegistration
                  ? 'Correo verificado. Completa tus datos y crea tu contraseña.'
                  : 'Correo verificado. Crea tu nueva contraseña.'}
          </p>
          <form onSubmit={submit}>
            {stage === 'email' && field('Correo electrónico', email, setEmail,
              { type: 'email', autoComplete: 'email' })}
            {stage === 'code' && (
              <label style={label}>
                Código de verificación
                <input value={code}
                  onChange={(event) => setCode(
                    event.target.value.replace(/\D/g, '').slice(0, 6)
                  )}
                  inputMode="numeric" autoComplete="one-time-code"
                  pattern="[0-9]{6}" maxLength={6} required
                  style={{ ...input, letterSpacing: 5, textAlign: 'center' }} />
              </label>
            )}
            {stage === 'details' && <>
              {isRegistration && <>
                {field('Nombre completo', name, setName,
                  { autoComplete: 'name', maxLength: 100 })}
                {field('Teléfono', phone, setPhone,
                  { type: 'tel', autoComplete: 'tel', maxLength: 30 })}
                {field('Dirección', address, setAddress,
                  { autoComplete: 'street-address', maxLength: 255 })}
              </>}
              {field('Contraseña (mínimo 8 caracteres)', password, setPassword,
                { type: 'password', autoComplete: 'new-password', minLength: 8 })}
              {field('Confirma tu contraseña', confirmation, setConfirmation,
                { type: 'password', autoComplete: 'new-password', minLength: 8 })}
            </>}
            {message && <p role="status" style={{ color: '#bfdbfe',
              fontSize: 13, lineHeight: 1.4 }}>{message}</p>}
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: 12, border: 0, borderRadius: 10,
                background: '#2563eb', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: 15 }}>
              {loading ? 'Procesando...' : stage === 'email' ? 'Enviar código'
                : stage === 'code' ? 'Verificar código'
                  : isRegistration ? 'Activar cuenta' : 'Guardar contraseña'}
            </button>
          </form>
          {stage !== 'email' && <div style={{ display: 'flex',
            justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
            <button type="button" disabled={loading} onClick={sendCode}
              style={{ border: 0, padding: 8, background: 'transparent',
                color: '#93c5fd', cursor: 'pointer' }}>
              Reenviar código
            </button>
            <button type="button" onClick={() => {
              localStorage.removeItem('maccez_account_code_pending');
              setStage('email'); setCode(''); setMessage('');
            }} style={{ border: 0, padding: 8, background: 'transparent',
              color: '#93c5fd', cursor: 'pointer' }}>
              Cambiar correo
            </button>
          </div>}
        </>
      )}
      <button type="button" onClick={onBack}
        style={{ display: 'block', margin: '10px auto 0', padding: 8,
          border: 0, background: 'transparent', color: '#93c5fd',
          cursor: 'pointer' }}>
        Volver a ingresar
      </button>
    </div>
  );
}

export default AccountAccessPanel;
